import { useCallback, useRef, useState } from "react";
import { useDebouncer } from "@tanstack/react-pacer";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Debounced autosave with an awaitable flush, for use before navigating
 * away or firing Approve/Flag/Delete. `save` may change identity across
 * renders (e.g. a new mutateAsync per script id) — the latest is always
 * used via a ref, so the debouncer itself stays stable. */
export function useAutosave<T>(save: (patch: T) => Promise<void>, waitMs = 500) {
  const [status, setStatus] = useState<SaveStatus>("idle");
  const pendingRef = useRef<Promise<void> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const debouncer = useDebouncer(
    (patch: T) => {
      setStatus("saving");
      pendingRef.current = saveRef
        .current(patch)
        .then(() => setStatus("saved"))
        .catch(() => setStatus("error"))
        .finally(() => {
          pendingRef.current = null;
        });
    },
    { wait: waitMs, onUnmount: d => d.flush() },
  );

  const schedule = useCallback((patch: T) => debouncer.maybeExecute(patch), [debouncer]);

  const flush = useCallback(async () => {
    debouncer.flush();
    await pendingRef.current;
  }, [debouncer]);

  return { schedule, flush, status };
}
